import Box from "@mui/material/Box";
import { Button, Dialog, DialogActions, DialogTitle, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useContext, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { GridToolbarContainer, GridActionsCellItem } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import TextField from '@mui/material/TextField';
import { useEffect } from "react";
import { ApiContext } from "../../../App";

const SolarGenPopUp = ({ api, facility, title, popUpData, setPopUpData, dialogIsOpen, setDialogIsOpen, cancelHandler, saveHandler, errorMessages, setErrorMessages, dialogHandler }) => {
    const { t } = useTranslation();

    
    return (
        <Dialog open={dialogIsOpen} fullWidth maxWidth={"md"} onClose={() => setDialogIsOpen(!dialogIsOpen)}>
            <form onSubmit={dialogHandler}>
                <Stack direction='column' spacing={2} sx={{ paddingLeft: '5%', paddingRight: '5%' }}>
                    <DialogTitle sx={{ padding: '16px 0px', paddingBottom: '0' }}>{title}</DialogTitle>
                    <TextField
                        style={{ marginTop: '0' }}
                        variant="standard"
                        id="name"
                        label={t("Name")}
                        value={popUpData.name}
                        onChange={(event) => setPopUpData({ ...popUpData, name: event.target.value })}
                        error={errorMessages.hasOwnProperty("name")}
                        helperText={errorMessages["name"] ? errorMessages["name"] : ""}
                    />
                    <TextField
                        style={{ marginTop: '0' }}
                        variant="standard"
                        id="kWp"
                        label={t("kWp")}
                        value={popUpData.kWp}
                        onChange={(event) => setPopUpData({ ...popUpData, kWp: event.target.value })}
                        error={errorMessages.hasOwnProperty("kWp")}
                        helperText={errorMessages["kWp"] ? errorMessages["kWp"] : ""}
                    />
                    <TextField
                        style={{ marginTop: '0' }}
                        variant="standard"
                        id="azimuth"
                        label={t("Azimuth")}
                        value={popUpData.azimuth}
                        onChange={(event) => setPopUpData({ ...popUpData, azimuth: event.target.value })}
                        error={errorMessages.hasOwnProperty("azimuth")}
                        helperText={errorMessages["azimuth"] ? errorMessages["azimuth"] : ""}
                    />
                    <TextField
                        style={{ marginTop: '0' }}
                        variant="standard"
                        id="declination"
                        label={t("Declination")}
                        value={popUpData.declination}
                        onChange={(event) => setPopUpData({ ...popUpData, declination: event.target.value })}
                        error={errorMessages.hasOwnProperty("declination")}
                        helperText={errorMessages["declination"] ? errorMessages["declination"] : ""}
                    />
                    <TextField
                        style={{ marginTop: '0' }}
                        variant="standard"
                        id="performance_ratio"
                        label={t("Performance Ratio")}
                        value={popUpData.performance_ratio}
                        onChange={(event) => setPopUpData({ ...popUpData, performance_ratio: event.target.value })}
                        error={errorMessages.hasOwnProperty("performance_ratio")}
                        helperText={errorMessages["performance_ratio"] ? errorMessages["performance_ratio"] : ""}
                    />
                    <DialogActions>
                        <Button onClick={cancelHandler} type="button">Cancel</Button>
                        <Button type="submit">Save</Button>
                    </DialogActions>
                </Stack>
            </form>
        </Dialog>
    )
}


const SolarTable = ({  facility }) => {
    const { t } = useTranslation();
    const [errorMessages, setErrorMessages] = useState({});
    const [solarRows, setSolarRows] = useState([])
    const [dialogIsOpen, setDialogIsOpen] = useState(false)
    const [dialogTitle, setDialogTitle] = useState('')
    const [popUpData, setPopUpData] = useState({
        azimuth: "",
        declination: "",
        kWp: "",
        location: "",
        capacities: "",
        longitude: "",
        latitude: "",
        name: "",
        performance_ratio: ""
    })
    const [facilityInfo, setFacilityInfo] = useState({})
    const { facilityApi, solarApi } = useContext(ApiContext)

    useEffect(() => {
        solarApi.list(facility,
            data => {
                setSolarRows(data)
            },
            () => setErrorMessages(t("Couldn't connect to server")))
            facilityApi.retrieve(facility, (data) => {
            setFacilityInfo(data)
        })
    }, [facility])

    const deleteHandler = (id) => {
        const confirmed = window.confirm(t('Delete this solar array?'))
        if (confirmed) {
            solarApi.delete(id,
                () => {
                    setSolarRows(solarRows.filter((row) => row.url !== id))
                },
                () => alert(t('Solar failed to be deleted'))
            )
        }
    }

    const editHandler = (id) => {
        setErrorMessages({})
        setPopUpData(solarRows.find((row) => row.url == id))
        setDialogTitle('Edit Solar Array')
        setDialogIsOpen(!dialogIsOpen)
    }

    const cancelHandler = () => {
        setDialogIsOpen(!dialogIsOpen)
    }


    const dialogHandler = (event) => {
        event.preventDefault()

        const errors = {}

        if (!popUpData.azimuth) {
            errors['azimuth'] = "Azimuth needs to be set"
        }

        if (popUpData.azimuth > 359 || popUpData.azimuth < 0) {
            errors['azimuth'] = "Azimuth needs to be between 0 and 359"
        }

        if (!popUpData.declination) {
            errors['declination'] = "Declination needs to be set"
        }

        if (popUpData.declination > 90 || popUpData.declination < -90) {
            errors['declination'] = "Declination needs to be between -90 and 90"
        }

        if (!popUpData.kWp) {
            errors['kWp'] = "kWp needs to be set"
        }

        if(popUpData.kWp < 0) {
            errors['kWp'] = "kWp cannot have a negative value"
        }

        if(popUpData.performance_ratio < 0 || popUpData.performance_ratio > 1) {
            errors['performance_ratio'] = "Performance ratio must be between 0 and 1"
        }

        if(!popUpData.performance_ratio) {
            errors['performance_ratio'] = "Performance ratio must be set"
        }

        if (!popUpData.name) {
            errors['name'] = "Name needs to be set"
        }

        
        if (Object.keys(errors) == 0){
            if (dialogTitle == 'Add Solar Array') {
                const newSolarArray = { 
                    azimuth: parseInt(popUpData.azimuth), 
                    declination: parseInt(popUpData.declination), 
                    facility, 
                    latitude: Number(facilityInfo.lat),
                    location: facilityInfo.name,
                    longitude: Number(facilityInfo.lon),
                    kWp: Number(popUpData.kWp),
                    name: popUpData.name, 
                    performance_ratio: popUpData.performance_ratio
                }
                solarApi.create(newSolarArray, (data) => {
                    setSolarRows([...solarRows, { ...newSolarArray, url: data.url }])
                    
                    setDialogIsOpen(!dialogIsOpen)
                }, () => {
                    alert(t('failed to create solar array'))
                })
            }
            if (dialogTitle == 'Edit Solar Array') {
                setDialogIsOpen(!dialogIsOpen)
                solarApi.update(popUpData.url, popUpData, () => setSolarRows(solarRows.map((row) => popUpData.url === row.url ? popUpData : row)), () => alert(t('Failed to edit solar array')))
            }
        } else {
            setErrorMessages(errors)
        }

        

    }

    const addSolarHandler = () => {
        setErrorMessages({})
        setDialogTitle('Add Solar Array')
        setPopUpData({
            azimuth: "",
            declination: "",
            kWp: "",
            location: "",
            capacities: "",
            inOperation: "",
            longitude: "",
            latitude: "",
            performance_ratio: ""
        })
        setDialogIsOpen(!dialogIsOpen)
    }


    const columns = [
        {
            field: "name",
            headerName: t("Name"),
            width: 350
        },
        {
            field: "location",
            headerName: t("Location"),
            width: 350
        },
        {
            field: "kWp",
            headerName: t("Module power (kW)"),
            width: 350
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: t('Actions'),
            flex: 0.5,
            maxWidth: 150,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label={t("Edit")}
                        className="textPrimary"
                        onClick={() => editHandler(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label={t("Delete")}
                        onClick={() => deleteHandler(id)}
                        color="inherit"
                    />,
                ];
            },
        },
    ]

    const gridToolbar = () => {
        return (
            <GridToolbarContainer>
                <Button color="primary" size="small" startIcon={<AddIcon />} onClick={addSolarHandler}>
                    {t("Add Solar Array")}
                </Button>
            </GridToolbarContainer>
        );
    }


    return (
        <Box sx={{ height: 400, width: '100%', marginTop: '20px' }}>
            <DataGrid
                getRowId={(row) => row.url}
                components={{
                    Toolbar: gridToolbar,
                }}
                rows={solarRows}
                columns={columns}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: 5,
                        },
                    },
                }}
                pageSizeOptions={[5]}
            />
            <SolarGenPopUp setDialogIsOpen={setDialogIsOpen} title={dialogTitle} errorMessages={errorMessages} setErrorMessages={setErrorMessages} dialogIsOpen={dialogIsOpen} setPopUpData={setPopUpData} popUpData={popUpData} cancelHandler={cancelHandler} saveHandler={dialogHandler} dialogHandler={dialogHandler} />
        </Box>
    );
}

export default SolarTable;